<script lang="ts">
  import Am5Loader from '@/Util/Am5Loader.svelte';
  import { onDestroy } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { getXertAm5, type am5Root } from '@/Util/XertAm5';

  type Point = { date: string; [metric: string]: number | string | null };
  export let actual: Point[] = [];
  export let predicted: Point[] = [];
  export let breakthroughs: { date: string; after: Record<string, number> | null; name?: string | null }[] = [];
  export let updates: { date: string }[] = [];
  export let metric: 'ftp' | 'hie' | 'pp' = 'ftp';
  export let unit = 'W';
  export let scale = 1;   // 0.001 shows joules as kJ

  const chartId = `plan-report-chart-${Math.random().toString(36).slice(2)}`;
  let element: HTMLElement;
  let root: am5Root | undefined;

  $: if (element && actual) rebuild(actual, predicted, breakthroughs, metric, updates, scale);

  // An arrow with an expression body: the bundler turns `return new Date(...).getTime()` in a function into `return;`.
  const toTime = (date: string) => new Date(date + 'T12:00:00').getTime();

  function rebuild(...args: unknown[]) {
    void args;
    root?.dispose();
    build();
  }

  function build() {
    const { am5, am5xy, MyTheme } = getXertAm5();
    am5.addLicense('AM5C87942260');
    root = am5.Root.new(element);
    root.setThemes([MyTheme.new(root)]);
    const chart = root.container.children.push(am5xy.XYChart.new(root, { paddingLeft: 0, paddingRight: 12, layout: root.verticalLayout }));

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 70 });
    xRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color('#6B7280') });
    xRenderer.grid.template.setAll({ strokeOpacity: 0.08 });
    const xAxis = chart.xAxes.push(am5xy.DateAxis.new(root, { baseInterval: { timeUnit: 'day', count: 1 }, renderer: xRenderer }));
    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.grid.template.setAll({ strokeOpacity: 0.08, strokeDasharray: [3, 4] });
    yRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color('#6B7280') });

    const values: number[] = [];
    const series = (name: string, rows: Point[], color: string, dashed: boolean) => {
      const data = rows.filter((r) => typeof r[metric] === 'number').map((r) => ({ t: toTime(r.date), v: (r[metric] as number) * scale }));
      data.forEach((d) => values.push(d.v));
      return { name, data, color, dashed };
    };
    const lines = [series($_('report.chart.actual'), actual, '#2563EB', false), series($_('report.chart.predicted'), predicted, '#9CA3AF', true)];
    breakthroughs.forEach((b) => { if (b.after && typeof b.after[metric] === 'number') values.push(b.after[metric] * scale); });
    const lo = values.length ? Math.min(...values) : 0;
    const hi = values.length ? Math.max(...values) : 1;
    const pad = Math.max((hi - lo) * 0.1, hi * 0.01, 0.5);
    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: yRenderer, min: lo - pad, max: hi + pad, strictMinMax: true }));
    yAxis.ghostLabel.set('minWidth', 36);

    for (const l of lines) {
      if (!l.data.length) continue;
      const s = chart.series.push(am5xy.LineSeries.new(root, { name: l.name, xAxis, yAxis, valueXField: 't', valueYField: 'v', stroke: am5.color(l.color),
        tooltip: am5.Tooltip.new(root, { labelText: `${l.name}: {valueY.formatNumber('#.0')} ${unit}` }) }));
      s.strokes.template.setAll({ strokeWidth: 2.5, strokeDasharray: l.dashed ? [5, 4] : [] });
      s.data.setAll(l.data);
    }

    // A breakthrough is a diamond at the signature it reached that day.
    const dots = breakthroughs.filter((b) => b.after && typeof b.after[metric] === 'number').map((b) => ({ t: toTime(b.date), v: b.after![metric] * scale, name: b.name ?? $_('report.chart.breakthrough') }));
    if (dots.length) {
      const bs = chart.series.push(am5xy.LineSeries.new(root, { name: $_('report.chart.breakthrough'), xAxis, yAxis, valueXField: 't', valueYField: 'v', stroke: am5.color('#00C853') }));
      bs.strokes.template.set('visible', false);
      bs.bullets.push(() => am5.Bullet.new(root!, { sprite: am5.Rectangle.new(root!, { width: 9, height: 9, rotation: 45, centerX: am5.p50, centerY: am5.p50, fill: am5.color('#00C853'),
        tooltipText: `{name}: {valueY.formatNumber('#.0')} ${unit}` }) }));
      bs.data.setAll(dots);
    }

    // Where the plan was updated: the prediction restarts from where the athlete was. A dashed vertical line in the legend.
    for (const u of updates) {
      const t = toTime(u.date);
      const line = chart.series.push(am5xy.LineSeries.new(root, { name: $_('report.chart.plan_updated'), xAxis, yAxis, valueXField: 't', valueYField: 'v', stroke: am5.color('#6B7280') }));
      line.strokes.template.setAll({ strokeWidth: 1.5, strokeDasharray: [4, 3], strokeOpacity: 0.8 });
      line.data.setAll([{ t, v: lo - pad }, { t, v: hi + pad }]);
    }

    chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none', xAxis }));
    const legend = chart.children.push(am5.Legend.new(root, { centerX: am5.p50, x: am5.p50, marginTop: 8 }));
    legend.labels.template.setAll({ fontSize: 12 });
    // One entry in the legend for every dashed line that marks where the plan was updated.
    const shown: string[] = [];
    legend.data.setAll(chart.series.values.filter((s) => { const n = s.get('name') as string; if (n !== $_('report.chart.plan_updated')) return true; if (shown.includes(n)) return false; shown.push(n); return true; }));
  }

  onDestroy(() => root?.dispose());
</script>

<Am5Loader>
  <div id={chartId} bind:this={element} class="w-full h-72" />
</Am5Loader>
